<template>
  <template v-if="showPurchaseOrders">
    <div
      v-if="fetchPurchaseOrdersAction.firstLoad.value"
      class="w-full overflow-y-auto relative"
    >
      <div class="flex flex-col w-full border border-gray-200 rounded-lg shadow-sm">
        <div class="px-2 py-4 bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 w-full">
                <div class="relative">
                  <AppInputIcon
                    v-model="searchForm.form.search"
                    type="text"
                    name="purchase_orders_search"
                    class="form-input w-full"
                    placeholder="Search purchase orders..."
                  >
                    <template #icon>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 text-gray-400">
  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
</svg>
                    </template>
                  </AppInputIcon>
                </div>

                <AppSelect
                  v-model="searchForm.form.status"
                  name="status_filter"
                  class="w-full capitalize form-input pr-8 bg-white border-gray-300"
                  :options="statusOptions"
                  empty-text="All statuses"
                />

                <AppSelect
                  v-model="searchForm.form.package_id"
                  name="package_filter"
                  class="w-full form-input pr-8 bg-white border-gray-300"
                  :options="packageOptions"
                  empty-text="All packages"
                />

                <button
                  type="button"
                  class="btn btn--secondary-blue w-full text-sm"
                  @click="resetSearch"
                >
                  Clear filters
                </button>
              </div>
            </div>

            <p
              v-if="totalCount > 0"
              class="text-sm text-gray-600"
            >
              <span class="font-medium text-gray-900">{{ totalCount }}</span> purchase order(s)
            </p>
          </div>
        </div>

        <table class="border-separate border-spacing-0 w-full z-10 text-sm">
          <thead>
            <tr class="child:px-2 child:py-3 sticky top-0 child:border-y child:border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">
              <th class="w-12" />

              <th class="text-left">
                Code
              </th>

              <th class="text-left">
                Date
              </th>

              <th class="text-left">
                Description
              </th>

              <th class="text-left">
                Contractor ref
              </th>

              <th class="text-right">
                Amount
              </th>

              <th class="text-right">
                Actual cost
              </th>

              <th class="text-left">
                Status
              </th>

              <th class="text-left">
                Package
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="fetchPurchaseOrdersAction.is(States.LOADING)">
              <td
                colspan="9"
                class="px-6 py-16 text-center"
              >
                <AppSpinner class="h-8 w-8 mx-auto" />
              </td>
            </tr>

            <template v-else-if="fetchPurchaseOrdersAction.is(States.COMPLETE) && filteredPurchaseOrders.length > 0">
              <PurchaseOrderRow
                v-for="purchaseOrder in filteredPurchaseOrders"
                :key="purchaseOrder.id"
                :purchase-order="purchaseOrder"
                @open-details="emit('open-details', $event)"
              />

              <tr v-if="hasMorePages || loadingMore">
                <td
                  colspan="9"
                  class="px-6 py-4 text-center"
                >
                  <AppSpinner
                    v-if="loadingMore"
                    class="h-6 w-6 mx-auto"
                  />

                  <div
                    v-else-if="hasMorePages"
                    ref="loadMoreSentinelRef"
                    class="h-4"
                  />
                </td>
              </tr>
            </template>

            <tr v-else-if="fetchPurchaseOrdersAction.is(States.COMPLETE)">
              <td
                colspan="9"
                class="px-6 py-16 text-center text-sm text-gray-500"
              >
                No purchase orders found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <AppSpinner
      v-else
      class="mx-auto mt-5"
    />
  </template>

  <div
    v-else-if="fetchPurchaseOrdersAction.is(States.LOADING)"
    class="flex w-full py-16"
  >
    <AppSpinner class="mx-auto" />
  </div>

  <div
    v-else
    class="flex flex-col items-center py-12 text-sm text-gray-500"
  >
    No purchase orders yet. Create your first purchase order to get started.
  </div>
</template>

<script setup lang="ts">
import PurchaseOrderRow from '@/components/HeadContract/PurchaseOrders/PurchaseOrderRow.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import PurchaseOrder, { PurchaseOrderStatus, PurchaseOrderStatusTitles } from '@/models/PurchaseOrder';
import { usePackagesStore } from '@/store/packages';
import { useProjectsStore } from '@/store/projects';
import { usePurchaseOrdersStore } from '@/store/purchaseOrders';
import { useDebounceFn, useIntersectionObserver } from '@vueuse/core';
import { useCollect } from 'pinia-orm/dist/helpers';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

const emit = defineEmits<{
  (e: 'open-details', purchaseOrder: PurchaseOrder): void;
}>();

const projectsStore = useProjectsStore();
const purchaseOrdersStore = usePurchaseOrdersStore();
const packagesStore = usePackagesStore();
const project = computed(() => projectsStore.project);
const totalCount = ref(0);
const loadedIds = ref<number[]>([]);
const currentPage = ref(0);
const lastPage = ref(1);
const loadingMore = ref(false);
const loadMoreSentinelRef = useTemplateRef('loadMoreSentinelRef');

const searchForm = useFormData({
  search: '',
  status: undefined as number | undefined,
  package_id: undefined as number | undefined,
});

async function resetAndLoad(
  projectId: Id,
  params: ApiRequests['api.v1.purchase-orders.index'] = {},
): Promise<void> {
  loadedIds.value = [];
  currentPage.value = 0;

  const paginator = await purchaseOrdersStore.fetchPurchaseOrders(projectId, { ...params, page: 1 });

  loadedIds.value = paginator.data.map((row: PurchaseOrder) => row.id);
  currentPage.value = paginator.meta.current_page;
  lastPage.value = paginator.meta.last_page;
  totalCount.value = paginator.meta.total;
}

const fetchPurchaseOrdersAction = useStoreApiAction(resetAndLoad);
const hasMorePages = computed(() => currentPage.value < lastPage.value);

const loadMore = async () => {
  const projectId = project.value?.id;

  if(!projectId || loadingMore.value || !hasMorePages.value || fetchPurchaseOrdersAction.is(States.LOADING)) {
    return;
  }

  loadingMore.value = true;

  try {
    const paginator = await purchaseOrdersStore.fetchPurchaseOrders(projectId, {
      ...searchForm.form,
      page: currentPage.value + 1,
    });

    loadedIds.value = [...loadedIds.value, ...paginator.data.map((row: PurchaseOrder) => row.id)];
    currentPage.value = paginator.meta.current_page;
    lastPage.value = paginator.meta.last_page;
  } finally {
    loadingMore.value = false;
  }
};

useIntersectionObserver(loadMoreSentinelRef, ([{ isIntersecting }]) => {
  if(isIntersecting) {
    loadMore();
  }
});

const filteredPurchaseOrders = computed(() => {
  return purchaseOrdersStore.models.with('package').whereIn('id', loadedIds.value).get();
});

const showPurchaseOrders = computed(() => {
  if(totalCount.value > 0) {
    return true;
  }

  return fetchPurchaseOrdersAction.firstLoad.value && searchForm.isDirty.value;
});

const statusOptions = computed(() => {
  return Object.entries(PurchaseOrderStatus).map(([key, value]) => ({
    value,
    label: PurchaseOrderStatusTitles[key as keyof typeof PurchaseOrderStatusTitles],
  }));
});

const packageOptions = computed(() => {
  if(!project.value?.id) {
    return [];
  }

  return packagesStore.models
    .where('project_id', project.value.id)
    .get()
    .map((pkg) => ({
      value: pkg.id,
      label: pkg.name,
    }));
});

const debouncedSearch = useDebounceFn(() => {
  if(project.value?.id) {
    fetchPurchaseOrdersAction.request(project.value.id, searchForm.form);
  }
}, 300);

watch(() => searchForm.form.search, debouncedSearch);

watch(() => searchForm.form.status, () => {
  if(project.value?.id) {
    fetchPurchaseOrdersAction.request(project.value.id, searchForm.form);
  }
});

watch(() => searchForm.form.package_id, () => {
  if(project.value?.id) {
    fetchPurchaseOrdersAction.request(project.value.id, searchForm.form);
  }
});

watch(() => project.value?.id, async (projectId) => {
  if(projectId) {
    await packagesStore.index(projectId);
    fetchPurchaseOrdersAction.request(projectId, searchForm.form);
  }
}, { immediate: true });

const resetSearch = () => {
  searchForm.resetData();
  searchForm.resetErrors();

  if(project.value?.id) {
    fetchPurchaseOrdersAction.request(project.value.id, searchForm.form);
  }
};

defineExpose({
  reload: () => {
    if(project.value?.id) {
      return fetchPurchaseOrdersAction.request(project.value.id, searchForm.form);
    }
  },
});
</script>
